/** @file This file contains the store for assignment analytics. */
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import { Workspace } from '@/models/analytics';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/**
 * Fetches and transforms all pages of a specific analytics data source for an assignment.
 *
 * @param assignmentId The ID of the assignment.
 * @param dataSourceName The name of the data source to fetch from the API.
 * @param cls The constructor of the expected data source class for runtime type validation.
 * @returns A promise that resolves with the fully loaded and transformed data.
 */
async function loadDataSource(assignmentId, dataSourceName, cls) {
    return await Array.fromAsync(utils.loadAllPages(nextToken => client.p.assignment.getAnalyticsData({
        assignmentId,
        dataSourceName,
        nextToken: nextToken.extract(),
    })), (el) => {
        if (!(el.data instanceof cls)) {
            throw new TypeError(`Expected a ${dataSourceName} element`);
        }
        return {
            workId: el.workId,
            data: el.data,
        };
    });
}
/** @returns The initial state. */
function makeInitialState() {
    return {
        byAssignment: {},
    };
}
const moduleBuilder = storeBuilder.module('analytics', makeInitialState());
/** The store for assignment analytics data. */
export var AnalyticsStore;
(function (AnalyticsStore) {
    /** Resets the analytics state to its initial values. */
    AnalyticsStore.commitClearAll = moduleBuilder.commit(state => Object.assign(state, makeInitialState()), 'commitClearAll');
    /** Commits the analytics workspace for a specific assignment. */
    const commitAnalytics = moduleBuilder.commit((state, { assignmentId, data, }) => {
        utils.vueSet(state.byAssignment, assignmentId, data);
    }, 'commitAnalytics');
    /** Gets the analytics workspace for a given assignment. */
    AnalyticsStore.getAnalytics = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param assignmentId The ID of the assignment.
     * @returns A `Maybe` containing the workspace if it exists.
     */
    (assignmentId) => Maybe.fromNullable(state.byAssignment[assignmentId]), 'getAnalytics');
    /**
     * Fetches all required analytics data sources for an assignment in parallel and
     * constructs the workspace.
     */
    AnalyticsStore.loadForAssignment = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param _ The module context (unused).
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async (_, { assignmentId, }) => {
        // TODO prevent multiple concurrent calls for the same assignment
        // loading everything multiple times.
        const [submissionData, feedbackData, rubricData] = await Promise.all([
            loadDataSource(assignmentId, 'submissions', api.SubmissionsAnalyticsData),
            loadDataSource(assignmentId, 'inline_feedback', api.InlineFeedbackAnalyticsData),
            loadDataSource(assignmentId, 'rubric_data', api.RubricAnalyticsData),
        ]);
        commitAnalytics({
            assignmentId,
            data: new Workspace(assignmentId, submissionData, feedbackData, rubricData),
        });
    }, 'loadForAssignment');
    // Reset the store when the user logs out to prevent stale data.
    utils.signal.AFTER_USER_CHANGED.registerSync(() => {
        AnalyticsStore.commitClearAll();
    });
})(AnalyticsStore || (AnalyticsStore = {}));
